import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';

interface PropertyItem {
  id: string;
  title: string;
  city: string;
  district: string;
  price: number;
  rooms: number;
  size: number;
  uploadedAgo: number; // minutes ago (simulated)
}

const MOCK_SEED: Omit<PropertyItem, 'id' | 'uploadedAgo'>[] = [
  { title: '大安區精裝兩房，近捷運，採光極佳', city: '台北市', district: '大安區', price: 24500, rooms: 2, size: 22 },
  { title: '信義區全新套房，家具全配，即可入住', city: '台北市', district: '信義區', price: 18800, rooms: 1, size: 14 },
  { title: '中山區整層三房，零仲介費直接租', city: '台北市', district: '中山區', price: 38000, rooms: 3, size: 35 },
  { title: '板橋精緻兩房，鄰近捷運新埔站', city: '新北市', district: '板橋區', price: 22000, rooms: 2, size: 20 },
  { title: '永和溫馨套房，近頂溪捷運，生活方便', city: '新北市', district: '永和區', price: 14500, rooms: 1, size: 10 },
  { title: '中正區文青兩房，日式裝潢，近師大', city: '台北市', district: '中正區', price: 27000, rooms: 2, size: 24 },
  { title: '內湖科技园區附近三房，停車位', city: '台北市', district: '內湖區', price: 42000, rooms: 3, size: 38 },
  { title: '新莊副都心豪華兩房，全新裝潢', city: '新北市', district: '新莊區', price: 25000, rooms: 2, size: 28 },
];

function formatAgo(minutes: number): string {
  if (minutes < 1) return '剛剛';
  if (minutes < 60) return `${minutes} 分鐘前`;
  const h = Math.floor(minutes / 60);
  return h === 1 ? '1 小時前' : `${h} 小時前`;
}

function roomLabel(rooms: number): string {
  if (rooms === 1) return '套房';
  if (rooms === 2) return '兩房';
  if (rooms === 3) return '三房';
  return `${rooms} 房`;
}

export default function LatestUploadsFeed() {
  const [items, setItems] = useState<PropertyItem[]>([]);
  const [newItemId, setNewItemId] = useState<string | null>(null);
  const [listingCount, setListingCount] = useState(0);
  const tickRef = useRef(0);
  const cycleRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch from Supabase, fallback to mock
  useEffect(() => {
    const fetchProperties = async () => {
      const { data, error } = await supabase
        .from('properties')
        .select('id, title, city, district, price, rooms, size')
        .order('created_at', { ascending: false })
        .limit(20);

      setListingCount(data?.length ?? 0);

      let source: PropertyItem[];
      if (!error && data && data.length >= 3) {
        source = data.slice(0, 8).map((p, i) => ({
          id: p.id,
          title: p.title || MOCK_SEED[i % MOCK_SEED.length].title,
          city: p.city || MOCK_SEED[i % MOCK_SEED.length].city,
          district: p.district || MOCK_SEED[i % MOCK_SEED.length].district,
          price: p.price || MOCK_SEED[i % MOCK_SEED.length].price,
          rooms: p.rooms || MOCK_SEED[i % MOCK_SEED.length].rooms,
          size: p.size || MOCK_SEED[i % MOCK_SEED.length].size,
          uploadedAgo: Math.floor(Math.random() * 45) + 2,
        }));
      } else {
        source = MOCK_SEED.map((m, i) => ({
          ...m,
          id: `mock-${i}`,
          uploadedAgo: i * 5 + Math.floor(Math.random() * 4) + 1,
        }));
      }

      setItems(source.slice(0, 4));
    };
    fetchProperties();
  }, []);

  // Auto-cycle: every 4s simulate a new upload appearing
  useEffect(() => {
    if (items.length === 0) return;

    const allPool = MOCK_SEED.map((m, i) => ({
      ...m,
      id: `pool-${i}`,
      uploadedAgo: 0,
    }));

    const schedule = () => {
      cycleRef.current = setTimeout(() => {
        tickRef.current = (tickRef.current + 1) % allPool.length;
        const next: PropertyItem = {
          ...allPool[tickRef.current],
          id: `pool-${tickRef.current}-${Date.now()}`,
          uploadedAgo: 0,
        };

        setNewItemId(next.id);
        setItems(prev => {
          // age all existing items by 1 min
          const aged = prev.map(p => ({ ...p, uploadedAgo: p.uploadedAgo + 1 }));
          // push new to front, keep last 4
          return [next, ...aged].slice(0, 4);
        });

        // Clear new indicator after 2s
        setTimeout(() => setNewItemId(null), 2000);
        schedule();
      }, 4200);
    };

    schedule();
    return () => { if (cycleRef.current) clearTimeout(cycleRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length > 0]);

  if (items.length === 0) return null;

  return (
    <div className="mt-10 rounded-2xl overflow-hidden border border-teal-100 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-teal-50">
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-teal-500"></span>
          </span>
          <p className="text-sm font-bold text-gray-800">最新上傳物件</p>
          <span className="text-[10px] text-teal-600 font-semibold bg-teal-50 px-2 py-0.5 rounded-full border border-teal-100">即時更新</span>
        </div>
        <div className="flex items-center gap-1.5 text-gray-400">
          <i className="ri-home-4-line text-sm"></i>
          <span className="text-xs">
            {listingCount > 0 ? `${listingCount.toLocaleString('zh-TW')} 間房源` : '持續增加中'}
          </span>
        </div>
      </div>

      {/* Feed list */}
      <div className="divide-y divide-gray-50 overflow-hidden">
        {items.map((item, idx) => (
          <div
            key={item.id}
            className="flex items-center gap-3.5 px-5 py-3.5 transition-all duration-500"
            style={{
              opacity: idx === 0 && newItemId === item.id ? 0.6 : 1,
              transform: idx === 0 && newItemId === item.id ? 'translateY(-4px)' : 'translateY(0)',
              animation: idx === 0 && newItemId === item.id ? 'slideIn 0.45s ease forwards' : undefined,
            }}
          >
            {/* Icon */}
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
              idx === 0 && newItemId === item.id
                ? 'bg-teal-500 text-white'
                : 'bg-teal-50 text-teal-500'
            }`}>
              <i className="ri-home-4-line text-base"></i>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate leading-snug">{item.title}</p>
              <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                <span className="text-[11px] text-gray-400">{item.city}{item.district}</span>
                <span className="text-gray-200">·</span>
                <span className="text-[11px] text-gray-400">{roomLabel(item.rooms)} / {item.size}坪</span>
              </div>
            </div>

            {/* Price + time */}
            <div className="flex-shrink-0 text-right">
              <p className="text-sm font-black text-gray-900">
                NT${item.price.toLocaleString('zh-TW')}
                <span className="text-[10px] font-normal text-gray-400">/月</span>
              </p>
              <p className={`text-[10px] mt-0.5 flex items-center justify-end gap-1 ${
                item.uploadedAgo === 0 ? 'text-teal-500 font-semibold' : 'text-gray-400'
              }`}>
                {item.uploadedAgo === 0 && <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse inline-block"></span>}
                {formatAgo(item.uploadedAgo)}上傳
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Bottom bar */}
      <div className="bg-teal-500 px-6 py-2.5 flex items-center justify-center gap-2">
        <i className="ri-sparkling-2-fill text-white/80 text-xs animate-pulse"></i>
        <p className="text-xs text-white/90 font-medium">
          屋主免費刊登・零仲介費・AI 自動配對租客
        </p>
        <i className="ri-sparkling-2-fill text-white/80 text-xs animate-pulse"></i>
      </div>

      <style>{`
        @keyframes slideIn {
          from { opacity: 0.4; transform: translateY(-10px); }
          to   { opacity: 1;   transform: translateY(0);    }
        }
      `}</style>
    </div>
  );
}
